import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fetchStores } from '../../api/stores';
import { fetchAttendanceHistory } from '../../api/attendance';
import { getErrorMessage } from '../../api/client';
import { API_BASE_URL } from '../../config';
import { resolveAttendancePhotoUrl } from '../../utils/resolveAttendancePhotoUrl';
import AttendancePhotoThumb from '../AttendancePhotoThumb';

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** Fetch image for PDF; returns data URL or null (CORS/network). */
async function fetchImageAsDataUrl(url, sameOrigin = false) {
  try {
    const res = await fetch(url, {
      mode: 'cors',
      credentials: sameOrigin ? 'include' : 'omit'
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    return await blobToDataURL(blob);
  } catch {
    return null;
  }
}

/** Load attendance photo through API (avoids CORS for R2 / cross-origin URLs). */
async function fetchAttendanceRecordImageAsDataUrl(backendUrl, authConfig, recordId) {
  if (!recordId || !backendUrl) return null;
  try {
    const base = backendUrl.replace(/\/$/, '');
    const { data } = await axios.get(`${base}/api/attendance/record/${recordId}/image`, {
      ...authConfig,
      responseType: 'blob'
    });
    if (!data || (typeof data.size === 'number' && data.size === 0)) return null;
    return await blobToDataURL(data);
  } catch {
    return null;
  }
}

function openHtmlInNewTab(html, onBlocked) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, '_blank');
  if (!w) {
    URL.revokeObjectURL(url);
    onBlocked?.();
    return null;
  }
  // Revoke after the tab has loaded the blob (delayed — URL must stay valid until loaded)
  const revokeLater = () => setTimeout(() => URL.revokeObjectURL(url), 120000);
  if (w.document?.readyState === 'complete') {
    revokeLater();
  } else {
    w.addEventListener('load', revokeLater, { once: true });
    setTimeout(revokeLater, 120000);
  }
  return w;
}

const EMPLOYEE_CACHE_TTL_MS = 60 * 1000;
let employeesCache = {
  employees: null,
  stores: null,
  fetchedAt: 0
};

export default function EmployeesManager({ authConfig, authToken, backendUrl, onUnauthorized }) {
  const base = backendUrl || API_BASE_URL;
  const [employees, setEmployees] = useState(() => employeesCache.employees || []);
  const [stores, setStores] = useState(() => employeesCache.stores || []);
  const [loading, setLoading] = useState(() => !employeesCache.employees);
  const [error, setError] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', storeId: '', role: '', pin: '', active: true });
  const [viewMode, setViewMode] = useState('grid');
  const [showAddModal, setShowAddModal] = useState(false);
  const [attendanceEmployee, setAttendanceEmployee] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [historyFrom, setHistoryFrom] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [historyTo, setHistoryTo] = useState(() => new Date().toISOString().slice(0, 10));
  const loadingRef = useRef(false);

  const load = useCallback(async ({ force = false } = {}) => {
    const cacheFresh = employeesCache.employees && Date.now() - employeesCache.fetchedAt < EMPLOYEE_CACHE_TTL_MS;
    if (!force && cacheFresh) {
      setEmployees(employeesCache.employees || []);
      setStores(employeesCache.stores || []);
      setLoading(false);
      return;
    }
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError('');
    try {
      const [empRes, storeList] = await Promise.all([
        axios.get(`${backendUrl}/api/employees`, authConfig),
        fetchStores().catch(() => [])
      ]);
      const nextEmployees = Array.isArray(empRes.data) ? empRes.data : [];
      const nextStores = Array.isArray(storeList) ? storeList : [];
      employeesCache = {
        employees: nextEmployees,
        stores: nextStores,
        fetchedAt: Date.now()
      };
      setEmployees(nextEmployees);
      setStores(nextStores);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to load employees');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [authConfig, backendUrl, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const loadAttendance = useCallback(async () => {
    if (!attendanceEmployee?.id || !authToken) return;
    setError('');
    try {
      const data = await fetchAttendanceHistory(authToken, {
        employeeId: attendanceEmployee.id,
        from: historyFrom,
        to: historyTo
      });
      setAttendanceHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setAttendanceHistory([]);
    }
  }, [attendanceEmployee?.id, authToken, historyFrom, historyTo, onUnauthorized]);

  useEffect(() => {
    if (attendanceEmployee && authToken) loadAttendance();
  }, [attendanceEmployee, authToken, historyFrom, historyTo, loadAttendance]);

  const handleViewAttendance = (emp) => {
    setAttendanceEmployee({ id: emp._id, name: emp.name || '' });
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setHistoryFrom(firstOfMonth.toISOString().slice(0, 10));
    setHistoryTo(now.toISOString().slice(0, 10));
  };

  const formatTime12 = useCallback((d) => {
    if (!d) return '';
    const h = d.getHours();
    const m = d.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  }, []);

  /** Build DTR rows + PDF body + month title */
  const buildDtrFromHistory = useCallback((history, fromStr, toStr) => {
    const fromDate = new Date(fromStr);
    const toDate = new Date(toStr);
    fromDate.setHours(0, 0, 0, 0);
    toDate.setHours(0, 0, 0, 0);
    const byDate = {};
    (history || []).forEach((r) => {
      if (!r.timestamp) return;
      const d = new Date(r.timestamp);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push({ type: r.type, ts: d });
    });
    Object.keys(byDate).forEach((k) => byDate[k].sort((a, b) => a.ts - b.ts));

    const getRowDataForDay = (d) => {
      const year = d.getFullYear();
      const month = d.getMonth();
      const day = d.getDate();
      const key = `${year}-${month}-${day}`;
      const punches = byDate[key] || [];
      const ins = punches.filter((p) => p.type === 'in').map((p) => p.ts);
      const outs = punches.filter((p) => p.type === 'out').map((p) => p.ts);
      let amArr = '';
      let amDep = '';
      let pmArr = '';
      let pmDep = '';
      if (ins.length >= 2 && outs.length >= 2) {
        amArr = formatTime12(ins[0]);
        amDep = formatTime12(outs[0]);
        pmArr = formatTime12(ins[1]);
        pmDep = formatTime12(outs[1]);
      } else if (ins.length >= 1 && outs.length >= 1) {
        if (ins[0].getHours() < 12) {
          amArr = formatTime12(ins[0]);
          pmDep = formatTime12(outs[0]);
        } else {
          pmArr = formatTime12(ins[0]);
          pmDep = formatTime12(outs[0]);
        }
      } else if (ins.length >= 1) {
        amArr = formatTime12(ins[0]);
      } else if (outs.length >= 1) {
        pmDep = formatTime12(outs[0]);
      }
      const dayNum = d.getDate();
      const monthLabel = d.toLocaleString('default', { month: 'short' });
      return { dayLabel: `${dayNum}/${monthLabel}`, amArr, amDep, pmArr, pmDep };
    };

    const htmlRows = [];
    const pdfBody = [];
    for (let d = new Date(fromDate); d <= toDate; d.setDate(d.getDate() + 1)) {
      const row = getRowDataForDay(new Date(d));
      htmlRows.push(
        `<tr><td>${row.dayLabel}</td><td>${row.amArr}</td><td>${row.amDep}</td><td>${row.pmArr}</td><td>${row.pmDep}</td><td></td><td></td></tr>`
      );
      pdfBody.push([row.dayLabel, row.amArr, row.amDep, row.pmArr, row.pmDep, '', '']);
    }

    const monthName =
      fromDate.getMonth() === toDate.getMonth()
        ? fromDate.toLocaleString('default', { month: 'long', year: 'numeric' })
        : `${fromDate.toLocaleString('default', { month: 'short' })} – ${toDate.toLocaleString('default', { month: 'long', year: 'numeric' })}`;

    return { rowsHtml: htmlRows.join(''), monthName, pdfBody };
  }, [formatTime12]);

  const buildPhotoSectionHtml = useCallback(
    (history) => {
      const rows = (history || [])
        .filter((r) => r.timestamp && r.facePhotoUrl)
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .map((r) => {
          const abs = resolveAttendancePhotoUrl(r.facePhotoUrl, base);
          const safeUrl = abs.replace(/"/g, '&quot;').replace(/</g, '');
          const t = new Date(r.timestamp).toLocaleString();
          const label = r.type === 'in' ? 'In' : 'Out';
          return `<tr><td style="padding:4px;border:1px solid #000">${t}</td><td style="padding:4px;border:1px solid #000">${label}</td><td style="padding:4px;border:1px solid #000;text-align:center"><img src="${safeUrl}" alt="" style="max-height:56px;max-width:72px;object-fit:cover;vertical-align:middle"/></td></tr>`;
        });
      if (rows.length === 0) {
        return '<p class="dtr-photo-note">No verification photos in this period.</p>';
      }
      return `
        <h2 class="dtr-photos-title">Attendance verification photos</h2>
        <table class="dtr-photo-table">
          <thead><tr><th>Date &amp; time</th><th>Type</th><th>Photo</th></tr></thead>
          <tbody>${rows.join('')}</tbody>
        </table>`;
    },
    [base]
  );

  const getDtrHtml = useCallback(() => {
    const toDate = historyTo || historyFrom;
    const { rowsHtml, monthName } = buildDtrFromHistory(attendanceHistory, historyFrom, toDate);
    const name = attendanceEmployee?.name || '';
    const photoBlock = buildPhotoSectionHtml(attendanceHistory);
    const copyHtml = `
      <div class="dtr-copy">
        <h1 class="dtr-title">DAILY TIME RECORD</h1>
        <p class="dtr-subtitle">---oOo---</p>
        <p class="dtr-field">(Name) ${name}</p>
        <p class="dtr-field">For the month of ${monthName} _________</p>
        <p class="dtr-field">Official hours for arrival and departure _________</p>
        <p class="dtr-field">Regular days _________</p>
        <p class="dtr-field">Saturdays _________</p>
        <table class="dtr-table">
          <thead>
            <tr><th rowspan="2">Day</th><th colspan="2">A.M.</th><th colspan="2">P.M.</th><th colspan="2">Undertime</th></tr>
            <tr><th>Arrival</th><th>Departure</th><th>Arrival</th><th>Departure</th><th>Hours</th><th>Minutes</th></tr>
          </thead>
          <tbody>${rowsHtml}<tr class="total-row"><td>Total</td><td colspan="2"></td><td colspan="2"></td><td colspan="2"></td></tr></tbody>
        </table>
        <p class="dtr-cert">I certify on my honor that the above is a true and correct report of the hours of work performed, record of which was made daily at the time of arrival and departure from office.</p>
        <p class="dtr-verify">VERIFIED as to the prescribed office hours:</p>
        <p class="dtr-sigline">_________________________________________</p>
        <p class="dtr-incharge">In Charge</p>
      </div>`;
    return `
      <!DOCTYPE html>
      <html>
      <head><meta charset="utf-8"><title>DTR - ${name}</title>
      <style>
        @media print{body{margin:0;padding:8px}}
        body{font-family:Arial,sans-serif;font-size:11px;margin:0;padding:8px}
        .dtr-wrapper{display:flex;gap:24px;justify-content:space-between;align-items:flex-start}
        .dtr-copy{flex:1;min-width:0;max-width:48%}
        .dtr-title{text-align:center;font-size:14px;font-weight:bold;margin:0 0 4px 0}
        .dtr-subtitle{text-align:center;margin:0 0 8px 0}
        .dtr-field{margin:2px 0;font-size:10px}
        .dtr-table{width:100%;border-collapse:collapse;margin:8px 0;font-size:9px}
        .dtr-table th,.dtr-table td{border:1px solid #000;padding:2px 4px;text-align:center}
        .dtr-table th{background:#f0f0f0}
        .total-row{font-weight:bold}
        .dtr-cert{margin-top:12px;font-size:9px;text-align:justify}
        .dtr-verify{margin-top:8px;font-size:10px}
        .dtr-sigline{margin:4px 0;border-bottom:1px solid #000;min-width:200px}
        .dtr-incharge{text-align:center;margin-top:4px;font-size:10px}
        .dtr-photos-section{margin-top:20px;clear:both;width:100%;page-break-inside:avoid}
        .dtr-photos-title{font-size:12px;margin:12px 0 8px 0}
        .dtr-photo-table{width:100%;border-collapse:collapse;font-size:9px;max-width:720px}
        .dtr-photo-table th,.dtr-photo-table td{border:1px solid #000;padding:4px}
        .dtr-photo-table th{background:#f0f0f0}
        .dtr-photo-note{font-size:10px;color:#444;margin-top:12px}
      </style></head>
      <body>
        <div class="dtr-wrapper">${copyHtml}${copyHtml}</div>
        <div class="dtr-photos-section">${photoBlock}</div>
      </body>
      </html>`;
  }, [attendanceHistory, historyFrom, historyTo, attendanceEmployee?.name, buildDtrFromHistory, buildPhotoSectionHtml]);

  const handleViewDtr = () => {
    setError('');
    const html = getDtrHtml();
    const w = openHtmlInNewTab(html, () =>
      setError('Could not open a new tab. Allow pop-ups for this site, or use Download DTR (PDF).')
    );
    if (w) w.focus();
  };

  const handleExtractForSignature = () => {
    setError('');
    const html = getDtrHtml();
    const w = openHtmlInNewTab(html, () =>
      setError('Could not open a new tab. Allow pop-ups for this site, or use Download DTR (PDF).')
    );
    if (w) {
      w.focus();
      setTimeout(() => {
        try {
          w.print();
        } catch {
          /* ignore */
        }
      }, 400);
    }
  };

  const generateDtrPdf = useCallback(async () => {
    const toDate = historyTo || historyFrom;
    const { pdfBody, monthName } = buildDtrFromHistory(attendanceHistory, historyFrom, toDate);
    const name = attendanceEmployee?.name || 'Employee';
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 40;
    let y = 36;

    doc.setFontSize(14);
    doc.text('DAILY TIME RECORD', pageW / 2, y, { align: 'center' });
    y += 22;
    doc.setFontSize(10);
    doc.text(`(Name) ${name}`, margin, y);
    y += 14;
    doc.text(`For the month of ${monthName}`, margin, y);
    y += 20;

    autoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      tableWidth: pageW - margin * 2,
      head: [
        [
          { content: 'Day', rowSpan: 2, styles: { halign: 'center', valign: 'middle' } },
          { content: 'A.M.', colSpan: 2, styles: { halign: 'center' } },
          { content: 'P.M.', colSpan: 2, styles: { halign: 'center' } },
          { content: 'Undertime', colSpan: 2, styles: { halign: 'center' } }
        ],
        ['Arrival', 'Departure', 'Arrival', 'Departure', 'Hours', 'Minutes']
      ],
      body: [...pdfBody, ['Total', '', '', '', '', '', '']],
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold' }
    });

    y = doc.lastAutoTable.finalY + 16;
    doc.setFontSize(8);
    const cert =
      'I certify on my honor that the above is a true and correct report of the hours of work performed, record of which was made daily at the time of arrival and departure from office.';
    const certLines = doc.splitTextToSize(cert, pageW - margin * 2);
    doc.text(certLines, margin, y);
    y += certLines.length * 10 + 12;
    doc.setFontSize(9);
    doc.text('VERIFIED as to the prescribed office hours:', margin, y);
    y += 20;
    doc.text('_________________________________________', margin, y);
    y += 14;
    doc.text('In Charge', margin + 80, y);
    y += 28;

    const photoRecords = (attendanceHistory || [])
      .filter((r) => r.timestamp && r.facePhotoUrl)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    doc.setFontSize(11);
    doc.text('Attendance verification photos', margin, y);
    y += 18;

    if (photoRecords.length === 0) {
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text('No verification photos in this period.', margin, y);
      doc.setTextColor(0);
    } else {
      const thumbW = 48;
      const thumbH = 48;

      for (const r of photoRecords) {
        const url = resolveAttendancePhotoUrl(r.facePhotoUrl, base);
        const line = `${new Date(r.timestamp).toLocaleString()} · ${r.type === 'in' ? 'In' : 'Out'}`;
        if (y > doc.internal.pageSize.getHeight() - thumbH - 50) {
          doc.addPage();
          y = 40;
        }
        doc.setFontSize(8);
        doc.text(line, margin, y + 8);

        let dataUrl = null;
        if (r._id && authToken && backendUrl) {
          dataUrl = await fetchAttendanceRecordImageAsDataUrl(backendUrl, authConfig, r._id);
        }
        if (!dataUrl) {
          let cred = false;
          try {
            cred =
              typeof window !== 'undefined' &&
              new URL(url, window.location.href).origin === window.location.origin;
          } catch {
            cred = false;
          }
          dataUrl = await fetchImageAsDataUrl(url, cred);
        }
        if (dataUrl) {
          const fmt = dataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
          try {
            const props = doc.getImageProperties(dataUrl);
            const scale = Math.min(thumbW / props.width, thumbH / props.height, 1);
            const iw = props.width * scale;
            const ih = props.height * scale;
            doc.addImage(dataUrl, fmt, pageW - margin - iw, y, iw, ih);
          } catch {
            doc.setFontSize(7);
            doc.text('(image error)', pageW - margin - 50, y + 8);
          }
        } else {
          doc.setFontSize(7);
          doc.setTextColor(120);
          doc.text('(photo unavailable)', pageW - margin - 72, y + 8);
          doc.setTextColor(0);
        }
        y += Math.max(thumbH, 22) + 8;
      }
    }

    const safeName = name.replace(/[^a-zA-Z0-9-_]/g, '_');
    doc.save(`DTR-${safeName}-${historyFrom}.pdf`);
  }, [
    attendanceHistory,
    historyFrom,
    historyTo,
    attendanceEmployee?.name,
    base,
    buildDtrFromHistory,
    authConfig,
    authToken,
    backendUrl
  ]);

  const handleDownloadDtr = async () => {
    setError('');
    setPdfLoading(true);
    try {
      await generateDtrPdf();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to generate PDF. Try again or use View DTR.'));
    } finally {
      setPdfLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ name: '', storeId: '', role: '', pin: '', active: true });
    setShowAddModal(false);
  };

  const handleEdit = (emp) => {
    setEditingId(emp._id);
    setForm({
      name: emp.name || '',
      storeId: emp.storeId || '',
      role: emp.role || '',
      pin: '',
      active: emp.active !== false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      name: form.name.trim(),
      storeId: form.storeId || undefined,
      role: form.role.trim() || undefined,
      active: form.active
    };
    if (form.pin) payload.pin = form.pin;
    try {
      if (editingId) {
        await axios.put(`${backendUrl}/api/employees/${editingId}`, payload, authConfig);
      } else {
        await axios.post(`${backendUrl}/api/employees`, payload, authConfig);
      }
      resetForm();
      await load({ force: true });
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to save employee');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    setError('');
    try {
      await axios.delete(`${backendUrl}/api/employees/${id}`, authConfig);
      if (editingId === id) resetForm();
      await load({ force: true });
    } catch (err) {
      if (err?.response?.status === 401 && onUnauthorized) onUnauthorized(err);
      setError(getErrorMessage(err) || 'Failed to delete employee');
    }
  };

  const employeePhoto = (employee) =>
    employee.photoUrl || employee.avatarUrl || employee.image || employee.profilePhotoUrl || '';

  const employeeInitials = (name = '') => {
    const parts = String(name).trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'E';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
  };

  const employeeStoreName = (employee) => {
    const id = employee.storeId || '';
    return stores.find((s) => String(s._id || s.id) === String(id))?.name || '';
  };

  const employeeActions = (employee) => (
    <>
      <button type="button" onClick={() => handleViewAttendance(employee)} className="text-sm border px-2 py-1 rounded">Attendance</button>
      <button type="button" onClick={() => handleEdit(employee)} className="text-sm border px-2 py-1 rounded">Edit</button>
      <button type="button" onClick={() => handleDelete(employee._id)} className="text-sm bg-red-600 text-white px-2 py-1 rounded">Delete</button>
    </>
  );

  return (
    <div className="space-y-6">
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {loading && (
        <p className="rounded border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
          {employees.length === 0 ? 'Loading employees...' : 'Refreshing employees...'}
        </p>
      )}
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="font-medium">Employees</h4>
            <p className="text-xs text-gray-500">Click <strong>Attendance</strong> on an employee to view history, then <strong>View DTR</strong> / <strong>Extract for signature</strong> / <strong>Download DTR</strong> (PDF).</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowAddModal(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
              title="Add employee"
            >
              <span className="text-lg leading-none">+</span>
              Add
            </button>
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-sm">
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`rounded-md px-3 py-1.5 ${viewMode === 'list' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                List
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`rounded-md px-3 py-1.5 ${viewMode === 'grid' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Grid
              </button>
            </div>
          </div>
        </div>

        {employees.length === 0 ? (
          <p className="rounded border p-3 text-gray-500">
            {loading ? 'Employee list is loading. You can still add or edit once data appears.' : 'No employees yet.'}
          </p>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {employees.map((u) => {
              const photo = employeePhoto(u);
              const storeName = employeeStoreName(u);
              return (
                <div key={u._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-16 w-16 shrink-0 overflow-hidden rounded-full bg-indigo-100 text-indigo-700 ring-2 ring-indigo-50">
                      {photo ? (
                        <img src={photo} alt={`${u.name} profile`} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-lg font-bold">
                          {employeeInitials(u.name)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h5 className="truncate font-semibold text-gray-900">{u.name}</h5>
                      {u.role ? <p className="truncate text-sm text-gray-500">{u.role}</p> : <p className="text-sm text-gray-400">No role</p>}
                      {storeName ? <p className="mt-1 truncate text-xs text-gray-500">{storeName}</p> : null}
                      {u.active === false ? (
                        <span className="mt-2 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Inactive</span>
                      ) : (
                        <span className="mt-2 inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Active</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {employeeActions(u)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <ul className="border rounded divide-y bg-white">
            {employees.map((u) => {
              const photo = employeePhoto(u);
              return (
                <li key={u._id} className="p-3 flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-full bg-indigo-100 text-indigo-700">
                      {photo ? (
                        <img src={photo} alt={`${u.name} profile`} className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm font-bold">
                          {employeeInitials(u.name)}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <span className="font-medium">{u.name}</span>
                      {u.role && <span className="text-sm text-gray-500 ml-2">({u.role})</span>}
                      {u.active === false && <span className="text-amber-600 text-sm ml-2">Inactive</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {employeeActions(u)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showAddModal && !editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="add-employee-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="add-employee-title" className="font-semibold text-gray-900">Add employee</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close add employee">×</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3 p-5">
              <input name="name" placeholder="Name" className="border w-full p-2 rounded" value={form.name} onChange={handleChange} required />
              <select name="storeId" className="border w-full p-2 rounded" value={form.storeId} onChange={handleChange}>
                <option value="">No store</option>
                {stores.map((s) => (
                  <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>
                ))}
              </select>
              <input name="role" placeholder="Role" className="border w-full p-2 rounded" value={form.role} onChange={handleChange} />
              <input name="pin" type="password" placeholder="PIN (optional)" className="border w-full p-2 rounded" value={form.pin} onChange={handleChange} />
              <label className="flex items-center gap-2">
                <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
                <span>Active</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm">Add employee</button>
                <button type="button" onClick={resetForm} className="flex-1 border px-4 py-2 rounded text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="edit-employee-title">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h4 id="edit-employee-title" className="font-semibold text-gray-900">Edit employee</h4>
              <button type="button" onClick={resetForm} className="text-2xl leading-none text-gray-400 hover:text-gray-700" aria-label="Close edit employee">×</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3 p-5">
              <input name="name" placeholder="Name" className="border w-full p-2 rounded" value={form.name} onChange={handleChange} required />
              <select name="storeId" className="border w-full p-2 rounded" value={form.storeId} onChange={handleChange}>
                <option value="">No store</option>
                {stores.map((s) => (
                  <option key={s._id || s.id} value={s._id || s.id}>{s.name}</option>
                ))}
              </select>
              <input name="role" placeholder="Role" className="border w-full p-2 rounded" value={form.role} onChange={handleChange} />
              <input name="pin" type="password" placeholder="New PIN (leave blank to keep)" className="border w-full p-2 rounded" value={form.pin} onChange={handleChange} />
              <label className="flex items-center gap-2">
                <input type="checkbox" name="active" checked={form.active} onChange={handleChange} />
                <span>Active</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded text-sm">Update</button>
                <button type="button" onClick={resetForm} className="flex-1 border px-4 py-2 rounded text-sm">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {attendanceEmployee && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="attendance-employee-title">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-xl bg-gray-50 p-4 shadow-2xl">
            <div className="sticky top-0 z-10 -mx-4 -mt-4 mb-4 flex flex-wrap items-center justify-between gap-4 border-b bg-white px-4 py-4">
              <h4 id="attendance-employee-title" className="font-semibold text-gray-900">Attendance: {attendanceEmployee.name}</h4>
              <button type="button" onClick={() => setAttendanceEmployee(null)} className="rounded border px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50">Close</button>
            </div>
          <p className="text-sm text-gray-600 mb-3">
            Set date range, then <strong>View DTR</strong> (new tab), <strong>Extract for signature</strong> (print), or <strong>Download DTR</strong> (PDF with photo thumbnails).
          </p>
          <div className="flex flex-wrap gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm">
              From
              <input type="date" value={historyFrom} onChange={(e) => setHistoryFrom(e.target.value)} className="border rounded px-2 py-1" />
            </label>
            <label className="flex items-center gap-2 text-sm">
              To
              <input type="date" value={historyTo} onChange={(e) => setHistoryTo(e.target.value)} className="border rounded px-2 py-1" />
            </label>
            <button
              type="button"
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                setHistoryTo(today);
              }}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Up to today
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-sm font-medium text-gray-700">DTR:</span>
            <button type="button" onClick={handleViewDtr} className="px-3 py-1.5 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-500">
              View DTR
            </button>
            <button type="button" onClick={handleExtractForSignature} className="px-3 py-1.5 border border-indigo-600 text-indigo-600 rounded text-sm hover:bg-indigo-50">
              Extract for signature (print)
            </button>
            <button
              type="button"
              onClick={handleDownloadDtr}
              disabled={pdfLoading}
              className="px-3 py-1.5 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-500 disabled:opacity-50"
            >
              {pdfLoading ? 'Building PDF…' : 'Download DTR (PDF)'}
            </button>
          </div>
          <div className="md:hidden space-y-3">
            {attendanceHistory.length === 0 && (
              <p className="text-gray-500 text-center py-4 border rounded bg-white">No attendance records for this period</p>
            )}
            {attendanceHistory.map((r) => {
              const photoSrc = r.facePhotoUrl
                ? resolveAttendancePhotoUrl(r.facePhotoUrl, backendUrl || API_BASE_URL)
                : '';
              const reportPhotoSrc = r.reportPhotoUrl
                ? resolveAttendancePhotoUrl(r.reportPhotoUrl, backendUrl || API_BASE_URL)
                : '';
              const when = r.timestamp ? new Date(r.timestamp).toLocaleString() : '-';
              return (
                <div key={r._id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
                      {r.type === 'in' ? 'Clock In' : 'Clock Out'}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{when}</p>
                  {r.workNote ? (
                    <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
                      <p className="text-xs font-medium uppercase text-gray-500">Work note</p>
                      <p className="mt-1 whitespace-pre-wrap">{r.workNote}</p>
                    </div>
                  ) : null}
                  {reportPhotoSrc ? (
                    <div className="mt-3 flex justify-center border-t border-gray-100 pt-3">
                      <AttendancePhotoThumb src={reportPhotoSrc} caption="Work report" altText={`Work report - ${when}`} />
                    </div>
                  ) : null}
                  {photoSrc ? (
                    <div className="mt-3 flex justify-center">
                      <AttendancePhotoThumb src={photoSrc} caption={when} altText={`Attendance — ${when}`} />
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-gray-400">No photo</p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="hidden md:block overflow-x-auto overflow-y-auto max-h-[min(24rem,70vh)] border rounded bg-white touch-pan-x">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-b bg-gray-100">
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Date & Time</th>
                  <th className="text-left p-2 min-w-[220px]">Work Note</th>
                  <th className="text-left p-2 w-[160px]">Photo</th>
                  <th className="text-left p-2 w-[160px]">Report Photo</th>
                </tr>
              </thead>
              <tbody>
                {attendanceHistory.length === 0 && (
                  <tr><td colSpan={5} className="p-4 text-gray-500 text-center">No attendance records for this period</td></tr>
                )}
                {attendanceHistory.map((r) => (
                  <tr key={r._id} className="border-b">
                    <td className="p-2">{r.type === 'in' ? 'Clock In' : 'Clock Out'}</td>
                    <td className="p-2">{r.timestamp ? new Date(r.timestamp).toLocaleString() : '-'}</td>
                    <td className="p-2 text-xs whitespace-pre-wrap">{r.workNote || '-'}</td>
                    <td className="p-2 align-top">
                      {r.facePhotoUrl ? (
                        <AttendancePhotoThumb
                          src={resolveAttendancePhotoUrl(r.facePhotoUrl, backendUrl || API_BASE_URL)}
                          caption={r.timestamp ? new Date(r.timestamp).toLocaleString() : undefined}
                          altText="Attendance verification photo"
                        />
                      ) : '-'}
                    </td>
                    <td className="p-2 align-top">
                      {r.reportPhotoUrl ? (
                        <AttendancePhotoThumb
                          src={resolveAttendancePhotoUrl(r.reportPhotoUrl, backendUrl || API_BASE_URL)}
                          caption="Work report"
                          altText="Work report photo"
                        />
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
